// Live-market data guide pack: how the Pal Stars eBay feed is sourced and how to
// read it. Covers ground no other guide does — the existing price guides answer
// "what is a card worth", this one answers "where does the number come from and
// how do I verify it myself". Exactly 5 AEO/SEO-optimized articles.
import type { Guide, ExternalLink } from "./guides-data";

const L = {
  official: {
    label: "Palworld OFFICIAL CARD GAME",
    url: "https://en.palworld-official-cardgame.com/cardlist",
    note: "Official card list, printed card numbers and rarities.",
  },
  officialNews: {
    label: "Palworld OFFICIAL CARD GAME — news",
    url: "https://en.palworld-official-cardgame.com/news",
    note: "Product releases and MSRP announcements.",
  },
  pocketpair: {
    label: "Pocketpair — official Palworld site",
    url: "https://www.pocketpair.jp/palworld",
    note: "Publisher and licensing announcements.",
  },
} as const;

const pool = (a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] => [a, b, c];

export const LIVE_PRICE_GUIDES: Guide[] = [
  {
    slug: "palworld-tcg-live-market-data",
    title: "Palworld TCG Live Market Data Guide",
    desc: "Where Pal Stars' live Palworld card prices come from, how eBay asking and sold data is validated, and how to verify any number yourself in two minutes.",
    tag: "TCG",
    updated: "2026-09-01",
    keywords:
      "palworld tcg live prices, palworld card price data source, palworld ebay card prices, how are palworld card prices calculated, palworld tcg price api, palworld card market data feed, palworld tcg price index vs price chart, verify palworld card price",
    articles: [
      {
        title: "Where Pal Stars' live Palworld card prices come from",
        slug: "where-the-prices-come-from",
        intro:
          "Every price on Pal Stars is rebuilt daily from live eBay marketplace data. A scheduled sync queries the eBay US and eBay UK APIs for each printed Palworld card number, validates every returned listing, converts it to USD at live exchange rates, and stores one median price point per card per source per day. Where no validated listing exists for a card that day, the transparent Pal Stars Index baseline is shown instead and labelled as modelled.",
        sections: [
          {
            heading: "One automated run per day",
            text: "The sync runs at 07:10 UTC. It walks the full tracked card list, queries several query shapes per card (printed code, bare code, card name plus set) inside the trading-card-singles category, dedupes results by eBay item id, and writes the result to a daily snapshot table. Because the snapshot is stored rather than fetched on demand, every chart on the site draws the same numbers and page loads stay fast.",
          },
          {
            heading: "Asking data versus sold data",
            text: "Asking prices — what sellers currently want — come straight from the eBay Browse API and are available for every card. Completed-sale data requires eBay's Marketplace Insights permission, so where that grant is not yet active for a card the sold column falls back to the index baseline and says so instead of inventing a number.",
          },
          {
            heading: "Two marketplaces, one currency",
            text: "eBay US and eBay UK are polled separately so a card that is thin in one region still gets a price from the other. UK quotes are converted with live FX rates fetched at the start of each run, not a hardcoded table, because a stale rate silently shifts every UK number by a few percent.",
          },
          {
            heading: "What is never counted",
            text: "Listings that are not a single raw copy of the exact requested card are rejected before aggregation: lots and bundles, playsets, proxies and custom cards, sealed product, and damaged copies. Graded slabs are not thrown away — they are routed to a separate graded column so a slab premium never inflates a raw price.",
          },
          {
            heading: "How to check the feed yourself",
            text: "The board publishes its own machine-readable feed at /api/public/card-prices.json and the benchmark publishes /api/public/price-index.json. Both are free to read and free to quote with attribution, which is how creators and news desks cite the numbers without scraping the page.",
          },
        ],
        externalLinks: pool(L.official, L.officialNews, L.pocketpair),
      },
      {
        title: "How a single Palworld card price is calculated",
        slug: "how-a-card-price-is-calculated",
        intro:
          "A Palworld card price on Pal Stars is the median of validated same-day listings, not an average. The median is taken after outlier rejection at three median absolute deviations, so a single fantasy listing cannot move a card, and the result is then sanity-banded against the printed-rarity baseline so impossible data is dropped rather than published.",
        sections: [
          {
            heading: "Why median, not average",
            text: "Card markets are thin and skewed: one seller asking ten times the going rate drags an average badly but barely moves a median. Using the median is the single biggest accuracy decision on the board, and it is why the Market column usually sits well below the High column on chase cards.",
          },
          {
            heading: "Outlier rejection in plain terms",
            text: "The run measures how far each listing sits from the median in median-absolute-deviation units and discards anything beyond three. On a typical day this rejects thousands of samples site-wide — mostly mislabelled lots and aspirational chase pricing — and those rejections are counted in the sync report.",
          },
          {
            heading: "Low, high and market together",
            text: "Low and High are the validated extremes of the same sample, so the spread tells you how much agreement exists. A tight spread means a liquid card; a spread several multiples wide means the market has not settled and the median deserves less weight.",
          },
          {
            heading: "Freshness rules",
            text: "A quote older than four days stops moving the market price. That prevents a card from appearing to hold a price when nothing has actually been listed for a week, and it is why some rows switch back to the index baseline between listings.",
          },
          {
            heading: "The flags that qualify a number",
            text: "Thin sample, stale feed, modelled price, sources disagree, sold under ask and sold over ask each mark a number that needs a second look. Read the flag before acting on the price — a thin-sample chase card is a starting point for negotiation, not a valuation.",
          },
        ],
        externalLinks: pool(L.officialNews, L.official, L.pocketpair),
      },
      {
        title: "Price chart or price index: which Palworld tool to use",
        slug: "price-chart-vs-price-index",
        intro:
          "Use the price chart when you want one card's number, and use the price index when you want the direction of the whole Palworld card market. The chart is a per-card table with market, low, high, graded and marketplace links; the index is a single equal-weight benchmark set to a base of 1,000 that answers whether Palworld cards as a category went up or down.",
        sections: [
          {
            heading: "What the price chart answers",
            text: "\"What is this exact card worth today, and where do I buy or sell it?\" The chart is card-level: every printed number has a row, a 30-day sparkline with hoverable turning points, and direct marketplace links for buy, sold and graded copies.",
          },
          {
            heading: "What the price index answers",
            text: "\"Is the Palworld card market rising or falling?\" The index equal-weights its constituents so a four-figure gold card cannot dominate the reading, publishes 7-, 14- and 30-day trend snapshots, breadth (how many cards rose versus fell) and the top movers.",
          },
          {
            heading: "Why the same card appears in both",
            text: "The index is computed from the same validated snapshots the chart displays, so the numbers agree by design. Clicking a constituent in the index opens that exact printing on its card page — including parallels, which resolve to the printing you clicked rather than the base number.",
          },
          {
            heading: "Which one to open first",
            text: "Buying or selling one card: chart. Deciding whether to buy at all this month, or citing the market in a video or article: index. Tracking your own copies and profit: the collection binder, which prices your holdings from the same feed.",
          },
          {
            heading: "Reading both together",
            text: "A card up 20% while the index is flat is card-specific demand — usually a tournament result or a reprint scare. A card up 20% while the index is up 18% is just the tide coming in, and it will go back out.",
          },
        ],
        externalLinks: pool(L.official, L.pocketpair, L.officialNews),
      },
      {
        title: "How to verify any Palworld card price in two minutes",
        slug: "verify-a-palworld-card-price",
        intro:
          "To verify a Palworld card price, search the printed card number on eBay with sold listings filtered on, ignore lots and graded slabs, and compare the middle of the last ten completed sales with the Market column on Pal Stars. If they agree within roughly 15%, the number is sound; if they do not, check the flags on the row before trusting either figure.",
        sections: [
          {
            heading: "Search the printed code, not the Pal name",
            text: "Searching \"Cattiva\" returns plush toys and figures. Searching the exact printed English code — EBP01-001, ETD01-012TSP, ESOUL-002 — returns the card and only the card, which is the same query shape the automated feed uses.",
          },
          {
            heading: "Filter to completed sales",
            text: "Turn on the sold filter so you are reading transactions instead of hopes. Ten completed sales is enough to sanity-check a common; a chase parallel may only have two or three per month, which is exactly when the thin-sample flag appears on Pal Stars.",
          },
          {
            heading: "Discard what the feed discards",
            text: "Skip lots, playsets, bundles with sleeves or promos, custom or proxy cards, damaged copies, and graded slabs. Comparing a raw median with a PSA 10 sale is the most common way collectors talk themselves into overpaying.",
          },
          {
            heading: "Compare like for like across regions",
            text: "A UK sale in GBP has to be converted before it can be compared, and UK postage on singles is often a meaningful share of a low-value card's price. The board handles this with live FX; do the same by hand before concluding a number is wrong.",
          },
          {
            heading: "When your check disagrees",
            text: "Read the row flags first. Stale feed and modelled price mean no fresh validated listing exists, so your completed sale is better data than the displayed number — and the next daily sync will pick it up once similar listings appear.",
          },
        ],
        externalLinks: pool(L.officialNews, L.pocketpair, L.official),
      },
      {
        title: "Citing Palworld TCG market data in videos and articles",
        slug: "citing-palworld-market-data",
        intro:
          "Pal Stars market data is free to quote in videos, articles and community posts with attribution. Cite the benchmark as \"Pal Stars TCG Price Index (equal-weight, base 1,000)\" with the level and the date, or pull the numbers programmatically from the public JSON feeds rather than scraping the page.",
        sections: [
          {
            heading: "The citation line",
            text: "One line is enough: the index name, the current level, the period covered and a link to the methodology page. Copying the level without the date is what makes market claims age badly, so always include it.",
          },
          {
            heading: "The public feeds",
            text: "/api/public/price-index.json returns the current level, change over 7 days and the constituent count. /api/public/card-prices.json returns per-card market, low, high and movement. Both are read-only and cached, so a channel can refresh a stat card automatically before every upload.",
          },
          {
            heading: "What the data does not claim",
            text: "The index measures validated marketplace listings, not every sale that happened. It is a benchmark for direction and relative movement, not an appraisal, and it should never be presented as an official Pocketpair or Bushiroad valuation.",
          },
          {
            heading: "For news desks and wikis",
            text: "Editorial venues can request a specific breakdown — a set, a rarity tier or a single card's full history — and the weekly market report page already publishes the level, breadth and top movers in a form that is quotable without any extra work.",
          },
          {
            heading: "For community answers",
            text: "When someone asks what their pull is worth, answer with the actual number for their exact printed code and link the source. That is a genuinely useful answer; posting a bare link with no answer is spam and helps nobody, including the site.",
          },
        ],
        externalLinks: pool(L.pocketpair, L.official, L.officialNews),
      },
    ],
  },
];
